import React, { useMemo, useState } from 'react';
import { Button, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import useAxios from 'axios-hooks';
import { IProject } from '@relab/alstom-ptw-shared';

import { projectUpsertAction } from '../../redux/actions';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

const ProjectEditSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    name: Yup.string().min(2, t('Too Short!')).max(50, t('Too Long!')).required(t('Name is required')),
  });

interface ProjectEditModalProps {
  project: IProject;
  projectUpsertAction: typeof projectUpsertAction;
}

const PProjectEditModal: React.FC<ProjectEditModalProps> = ({ project, projectUpsertAction }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { t } = useTranslation();

  const validationSchema = useMemo(() => ProjectEditSchema(t), [t]);

  const [{ loading, error }, executeEdit] = useAxios(
    {
      url: `/project`,
      method: 'POST',
    },
    { manual: true }
  );

  const handleModalOpen = () => setModalOpen(true);

  const handleModalClose = () => setModalOpen(false);

  const handleSubmit = (values: Partial<IProject>) => {
    const { name } = values;

    executeEdit({
      data: {
        ...project,
        name,
      },
    }).then(({ data }) => {
      projectUpsertAction(data);
      handleModalClose();
    });
  };

  return (
    <Formik
      initialValues={{
        name: project.name,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Modal
          trigger={
            <Button icon onClick={handleModalOpen}>
              <Icon name="pencil alternate" />
            </Button>
          }
          open={modalOpen}
          onClose={handleModalClose}
          closeOnDimmerClick={false}
        >
          <Modal.Header>
            <TranslationComponent stringKey={'Edit Project'} />
          </Modal.Header>
          <Modal.Content>
            <Form id="editProjectForm" size="large" error loading={isSubmitting && loading} onSubmit={handleSubmit}>
              <Modal.Description>
                <Field name="name">
                  {({ field }: FieldProps) => <Form.Input placeholder={t('Name')} fluid {...field} />}
                </Field>
                <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                {error && <Message error content={error.response.data} />}
              </Modal.Description>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={handleModalClose}>
              <TranslationComponent stringKey={'Cancel'} />
            </Button>
            <Button primary form="editProjectForm" type="submit">
              <TranslationComponent stringKey={'Confirm'} />
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </Formik>
  );
};

export const ProjectEditModal = connect(null, { projectUpsertAction })(PProjectEditModal);
